import React, { useEffect, useRef, useState } from "react";
import "./StuStyles/StudentNav.css";
import logo from "../../assets/logo.jpg";

import {
  FaBell,
  FaUserCircle,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaRedo,
} from "react-icons/fa";

import { FiLogOut } from "react-icons/fi";

import { useNavigate } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ApiService from "../../nhrc_apis/ApiService";

const { PasswordAPI } = ApiService;

function StudentNav() {

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [studentName, setStudentName] =
    useState("Student Name");

  const [user, setUser] =
    useState(null);

  const navigate = useNavigate();

  const dropdownRef = useRef(null);

  /* =========================================================
     CHANGE PASSWORD MODAL STATES

     Step 1: email -> PasswordAPI.forgotPassword(email)
             (backend emails an OTP)
     Step 2: OTP + new password + confirm password ->
             PasswordAPI.resetPassword({ email, otp,
             new_password, confirm_password }) in one call.
     Email is pre-filled from the logged-in student (editable).
  ========================================================= */

  const [showChangePwModal, setShowChangePwModal] = useState(false);
  const [changePwStep, setChangePwStep] = useState(1);

  const [changePwEmail, setChangePwEmail] = useState("");
  const [sendingChangePwOtp, setSendingChangePwOtp] = useState(false);

  const [changePwOtp, setChangePwOtp] = useState("");
  const [changePwNewPassword, setChangePwNewPassword] = useState("");
  const [changePwConfirmPassword, setChangePwConfirmPassword] = useState("");
  const [showChangePwNewPassword, setShowChangePwNewPassword] = useState(false);
  const [resettingChangePw, setResettingChangePw] = useState(false);

  /* =========================================================
     LOAD LOGGED IN USER
     Reads "studentLoggedInUser", a key ONLY StudentLogin.js
     writes to.
  ========================================================= */

  useEffect(() => {

    try {

      const storedUser =
        localStorage.getItem(
          "studentLoggedInUser"
        );

      if (storedUser) {

        const parsedUser =
          JSON.parse(storedUser);

        setUser(parsedUser);

        const fullName =
          parsedUser?.name ||
          parsedUser?.full_name ||
          parsedUser?.email ||
          "Student Name";

        setStudentName(fullName);
      }

    } catch (error) {

      console.log(
        "USER PARSE ERROR:",
        error
      );
    }

  }, []);

  useEffect(() => {

    const handleOutsideClick = (event) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target
        )
      ) {

        setMenuOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };

  }, []);

  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = () => {

    localStorage.removeItem("studentToken");
    localStorage.removeItem("token");
    localStorage.removeItem("studentMember");
    localStorage.removeItem("studentLoggedInUser");
    localStorage.removeItem("member");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("adminToken");

    navigate("/");
  };

  /* =========================================================
     CHANGE PASSWORD HANDLERS
  ========================================================= */

  const openChangePwModal = () => {
    setShowChangePwModal(true);
    setChangePwStep(1);
    setChangePwEmail(user?.email || "");
    setChangePwOtp("");
    setChangePwNewPassword("");
    setChangePwConfirmPassword("");
    setShowChangePwNewPassword(false);
  };

  const closeChangePwModal = () => {
    setShowChangePwModal(false);
  };

  /* STEP 1 — send OTP */
  const handleSendChangePwOtp = async () => {
    if (!changePwEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    try {
      setSendingChangePwOtp(true);

      await PasswordAPI.forgotPassword(
        changePwEmail.trim().toLowerCase()
      );

      toast.success("OTP sent to your email");

      setChangePwStep(2);
    } catch (err) {
      console.error("CHANGE PASSWORD - SEND OTP ERROR:", err);

      toast.error(
        err?.response?.data?.detail ||
          "Failed to send OTP. Please check the email and try again."
      );
    } finally {
      setSendingChangePwOtp(false);
    }
  };

  /* STEP 2 — verify OTP + set new password in one call */
  const handleChangePassword = async () => {
    if (!changePwOtp.trim()) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!changePwNewPassword.trim() || !changePwConfirmPassword.trim()) {
      toast.error("Please fill both password fields.");
      return;
    }

    if (changePwNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (changePwNewPassword !== changePwConfirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setResettingChangePw(true);

      await PasswordAPI.resetPassword({
        email: changePwEmail.trim().toLowerCase(),
        otp: changePwOtp.trim(),
        new_password: changePwNewPassword.trim(),
        confirm_password: changePwConfirmPassword.trim(),
      });

      toast.success("Password changed successfully!");

      closeChangePwModal();
    } catch (err) {
      console.error("CHANGE PASSWORD - RESET ERROR:", err);

      toast.error(
        err?.response?.data?.detail || "Failed to change password"
      );
    } finally {
      setResettingChangePw(false);
    }
  };

  return (

    <nav className="student-nav">

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="student-nav-logo">
        <div className="student-nav-logo-box">
          <img
            src={logo}
            alt="NHRC Logo"
            className="student-nav-logo-img"
          />
        </div>
        <span className="student-nav-logo-label">
          National Human Resource Club (NHRC)
        </span>
      </div>

      <div className="student-nav-left">

        Student Name :
        <span
          style={{
            marginLeft: "8px",
            fontWeight: "700",
            color: "#fff",
          }}
        >
          {studentName}
        </span>

      </div>

      <div className="student-nav-right">

        <FaBell
          className="student-nav-icon"
        />

        <div
          className="profile-wrapper"
          ref={dropdownRef}
        >

          <FaUserCircle
            className="student-nav-icon"
            onClick={() =>
              setMenuOpen(!menuOpen)
            }
          />

          {menuOpen && (

            <div className="profile-dropdown">

              <p
                onClick={() => {

                  navigate(
                    "/student/StudentProfile"
                  );

                  setMenuOpen(false);
                }}
              >

                <span className="icon">
                  👤
                </span>

                My Profile/Edit

              </p>

              <p
                onClick={() => {

                  openChangePwModal();

                  setMenuOpen(false);
                }}
              >

                <span className="icon">
                  🔒
                </span>

                Change Password

              </p>

              <p
                className="logout"
                onClick={handleLogout}
              >

                <FiLogOut
                  style={{
                    marginRight: "5px",
                  }}
                />

                Log Out

              </p>

            </div>
          )}

        </div>

      </div>

      {/* =========================================================
          CHANGE PASSWORD MODAL
      ========================================================= */}

      {showChangePwModal && (
        <div
          className="stuchangepw-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="stuchangepw-modal-card"
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px",
              width: "100%",
              maxWidth: "400px",
              position: "relative",
              color: "#333",
            }}
          >
            <button
              type="button"
              className="stuchangepw-modal-close"
              onClick={closeChangePwModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h4 style={{ marginBottom: "8px" }}>Change Password</h4>

            {/* ============ STEP 1: EMAIL ============ */}
            {changePwStep === 1 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Confirm your email address and we'll send you an OTP.
                </p>

                <input
                  type="email"
                  placeholder="Enter your email"
                  value={changePwEmail}
                  onChange={(e) => setChangePwEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {sendingChangePwOtp ? "Sending OTP..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ============ STEP 2: OTP + NEW PASSWORD ============ */}
            {changePwStep === 2 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Enter the OTP sent to <b>{changePwEmail}</b> and your new password.
                </p>

                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={changePwOtp}
                  onChange={(e) => setChangePwOtp(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <div style={{ position: "relative", marginBottom: "12px" }}>
                  <input
                    type={showChangePwNewPassword ? "text" : "password"}
                    placeholder="New password"
                    value={changePwNewPassword}
                    onChange={(e) => setChangePwNewPassword(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 40px 10px 12px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowChangePwNewPassword(!showChangePwNewPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "10px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    {showChangePwNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <input
                  type={showChangePwNewPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={changePwConfirmPassword}
                  onChange={(e) => setChangePwConfirmPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <button
                  type="button"
                  disabled={resettingChangePw}
                  onClick={handleChangePassword}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "8px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {resettingChangePw ? "Changing..." : "Change Password"}
                </button>

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#333",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <FaRedo />
                  Resend OTP
                </button>
              </>
            )}

          </div>
        </div>
      )}

    </nav>
  );
}

export default StudentNav;